/**
 * Context actions for the ⌘K palette (docs/ux/review/dev-power-user.md #1). A screen that owns
 * actions (the open session: Land, Allow once, Stop, Copy branch, Open in editor…) registers them
 * here while it is mounted; the palette lists them first. The actions call the screen's own
 * handlers, so the palette never re-implements an action.
 */
import { useEffect, useSyncExternalStore } from 'react'

export interface PaletteAction {
  id: string
  label: string
  /** Shown on the right: a shortcut label or the value an action copies. */
  hint?: string
  /** Extra words the fuzzy match should find ("approve" for "Allow once"). */
  keywords?: string
  run: () => void
}

const owners = new Map<string, readonly PaletteAction[]>()
const listeners = new Set<() => void>()
let snapshot: readonly PaletteAction[] = []

function publish() {
  snapshot = [...owners.values()].flat()
  for (const l of listeners) l()
}

export function setPaletteActions(owner: string, actions: readonly PaletteAction[] | undefined) {
  if (actions?.length) owners.set(owner, actions)
  else if (!owners.delete(owner)) return
  publish()
}

export function contextActions(): readonly PaletteAction[] {
  return snapshot
}

/** Register `actions` under `owner` while the calling component is mounted. */
export function usePaletteActions(owner: string, actions: readonly PaletteAction[]) {
  useEffect(() => {
    setPaletteActions(owner, actions)
  }, [owner, actions])
  useEffect(() => () => setPaletteActions(owner, undefined), [owner])
}

export function useContextActions(): readonly PaletteAction[] {
  return useSyncExternalStore(
    (cb) => {
      listeners.add(cb)
      return () => listeners.delete(cb)
    },
    contextActions,
    contextActions,
  )
}
